import type { LoginData, LoginResult } from '@/pages/login/model';
import { request } from '@/utils/request';

/**
 * 登录
 * 这里只负责调用后端 /auth/login，返回 token 和基础用户信息；
 * 具体权限在登录成功后，通过 getUserRefreshPermissions 再获取，避免在 token 未落库前就请求受保护接口导致 401。
 * @param data - 请求数据
 */
export async function login(data: LoginData): Promise<{ code: number; data: LoginResult }> {
  try {
    // 1. 调用后端登录接口（后端使用 email 字段，前端使用 username 字段）
    const res = await request.post('/auth/login', {
      email: data.username,
      password: data.password,
    });

    // 2. 适配后端返回格式
    // 后端返回格式：{ access_token: string, user: {...} }
    // 经过 request 拦截器后，res 就是后端返回的数据（res.data）
    const result = (res as any)?.data || res;
    
    // 检查返回数据格式
    if (!result || !result.access_token) {
      console.error('登录响应格式错误:', res);
      throw new Error('登录失败：服务器返回格式错误');
    }

    // 3. 先只返回 token 和基础用户信息，permissions 由后续刷新接口获取
    const loginResult: LoginResult = {
      token: result.access_token,
      user: {
        id: result.user?.id || '',
        username: result.user?.name || result.user?.email || '',
        phone: '',
        email: result.user?.email || '',
        roles: [result.user?.role || 'user'],
      },
      permissions: [],
      roles: [{ id: result.user?.role || 'user' }],
    };

    return {
      code: 200,
      data: loginResult,
    };
  } catch (error: any) {
    console.error('登录错误:', error);
    // 如果是已知的错误，直接抛出
    if (error?.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    if (error?.message) {
      throw error;
    }
    throw new Error('登录失败，请检查邮箱和密码');
  }
}

/**
 * 修改密码（已登录用户）
 * @param data - 请求数据 { oldPassword, newPassword }
 */
export function updatePassword(data: { oldPassword: string; newPassword: string }) {
  return request.post('/system/user/updatePassword', {
    oldPassword: data.oldPassword,
    newPassword: data.newPassword,
  });
}

/**
 * 忘记密码
 * @param data - 请求数据
 */
export function forgetPassword(data: object) {
  return request.post('/system/user/forgetPassword', data);
}
